import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, Send, Star, Truck, Users, X } from "lucide-react";
import { fmt, QuoteLines } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { contact_name: "", email: "", phone: "", event_date: "", guest_count: 100, menu_id: "", venue_name: "", venue_address: "", notes: "", website: "" };

export default function HubPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/hub/${slug}`).then((r) => setData(r.data)).catch(() => setError("This business hub doesn't exist or is offline."));
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    const c = data.company;
    document.title = c.meta_title || `${c.name} — Event Catering`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", c.meta_description || c.subheadline || `${c.name} — event catering`);
    return () => { document.title = "CateringDiary.app"; };
  }, [data]);

  if (error) return <div className="flex min-h-screen items-center justify-center bg-background"><p data-testid="hub-error" className="border border-red-300 bg-red-50 px-6 py-4 text-sm text-red-700">{error}</p></div>;
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-background"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const c = data.company;
  const accent = c.accent_color || "#059669";
  const PRESETS = {
    light: { page: "#f8fafc", text: "#0f172a", muted: "#64748b", card: "#ffffff", line: "#e2e8f0" },
    warm: { page: "#faf5ee", text: "#292524", muted: "#78716c", card: "#fffdf9", line: "#e7ded2" },
    dark: { page: "#0c1220", text: "#e2e8f0", muted: "#94a3b8", card: "#141d31", line: "#22304a" },
  };
  const P = PRESETS[c.theme] || PRESETS.light;
  const pageBg = (c.colors && c.colors.page_bg) || P.page;
  const textCol = (c.colors && c.colors.text) || P.text;
  const DAYS_ORDER = [["mon", "Mon"], ["tue", "Tue"], ["wed", "Wed"], ["thu", "Thu"], ["fri", "Fri"], ["sat", "Sat"], ["sun", "Sun"]];
  const hasHours = c.opening_hours && DAYS_ORDER.some(([k]) => { const d = c.opening_hours[k]; return d && (d.closed || (d.open && d.close)); });
  const dateBlocked = form.event_date && (data.blocked_dates || []).includes(form.event_date);
  const dateConflict = form.event_date && !dateBlocked && data.booked_dates.includes(form.event_date);
  const capacityExceeded = (data.total_capacity || 0) > 0 && Number(form.guest_count) > data.total_capacity;

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data: res } = await axios.post(`${API}/public/hub/${slug}/enquiry`, {
        ...form,
        guest_count: Number(form.guest_count),
        menu_id: form.menu_id || null,
        venue_name: form.venue_name,
        venue_address: form.venue_address,
      });
      setResult(res);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not send enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: pageBg, color: textCol }} data-testid="hub-page">
      <section className="relative bg-ink text-white">
        {c.hero_image && (
          <div className="absolute inset-0 overflow-hidden">
            <img src={c.hero_image} alt="" className="h-full w-full object-cover opacity-30" />
          </div>
        )}
        <div className="relative mx-auto max-w-4xl px-6 py-20 lg:py-28">
          <p className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: accent }}>{c.name}</p>
          <h1 className="mt-4 font-heading text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl" data-testid="hub-headline">{c.headline}</h1>
          {c.subheadline && <p className="mt-5 max-w-xl text-base text-white/70 md:text-lg">{c.subheadline}</p>}
          {(c.phone || c.contact_email) && (
            <p className="mt-6 text-sm text-white/60">{[c.phone, c.phone_secondary, c.contact_email].filter(Boolean).join(" · ")}</p>
          )}
          <a href="#enquire" data-testid="hub-enquire-cta" className="mt-8 inline-block px-6 py-3 font-heading text-sm font-bold uppercase tracking-wide text-white" style={{ backgroundColor: accent }}>
            Check your date
          </a>
          <a href={`/hub/${c.slug}/order`} data-testid="hub-order-cta" className="mt-8 ml-3 inline-block border border-white/40 px-6 py-3 font-heading text-sm font-bold uppercase tracking-wide text-white transition-colors duration-150 hover:bg-white/10">
            Order buffet pickup
          </a>
        </div>
      </section>

      {c.about && (
        <section className="mx-auto max-w-4xl px-6 py-14">
          <h2 className="font-heading text-xl font-bold tracking-tight">About us</h2>
          <p className="mt-4 whitespace-pre-line text-base leading-relaxed" style={{ color: P.muted }} data-testid="hub-about">{c.about}</p>
        </section>
      )}

      {(c.why_choose_us || []).length > 0 && (
        <section className="mx-auto max-w-4xl px-6 pb-14" data-testid="hub-why">
          <h2 className="font-heading text-xl font-bold tracking-tight">Why choose us</h2>
          <ul className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {c.why_choose_us.map((w, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" style={{ color: P.muted }} data-testid={`hub-why-item-${i}`}>
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" style={{ color: accent }} />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {c.services_blurb && (
        <section className="mx-auto max-w-4xl px-6 pb-14" data-testid="hub-services">
          <h2 className="font-heading text-xl font-bold tracking-tight">Services &amp; coverage</h2>
          <p className="mt-4 whitespace-pre-line text-base leading-relaxed" style={{ color: P.muted }}>{c.services_blurb}</p>
        </section>
      )}

      {(c.gallery || []).length > 0 && (
        <section className="py-14" style={{ backgroundColor: P.card, borderTop: `1px solid ${P.line}` }} data-testid="hub-gallery">
          <div className="mx-auto max-w-4xl px-6">
            <h2 className="font-heading text-xl font-bold tracking-tight">Gallery</h2>
            <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3">
              {c.gallery.map((url, i) => (
                <img key={i} src={url} alt="" loading="lazy" data-testid={`hub-gallery-img-${i}`} onClick={() => setLightbox(i)} className="aspect-[4/3] w-full cursor-zoom-in object-cover transition-opacity duration-150 hover:opacity-90" />
              ))}
            </div>
          </div>
        </section>
      )}

      {c.show_reviews && (c.reviews || []).length > 0 && (
        <section className="mx-auto max-w-4xl px-6 pb-14" data-testid="hub-reviews">
          <h2 className="font-heading text-xl font-bold tracking-tight">What customers say</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {c.reviews.map((r, i) => (
              <blockquote key={i} className="p-5" style={{ backgroundColor: P.card, border: `1px solid ${P.line}` }} data-testid={`hub-review-${i}`}>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${r.rating >= n ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />)}
                </div>
                {r.comment && <p className="mt-2 text-sm" style={{ color: P.muted }}>&ldquo;{r.comment}&rdquo;</p>}
                <footer className="mt-2 text-xs font-semibold" style={{ color: accent }}>— {r.customer_name}</footer>
              </blockquote>
            ))}
          </div>
        </section>
      )}

      {hasHours && (
        <section className="mx-auto max-w-4xl px-6 pb-14" data-testid="hub-hours">
          <h2 className="font-heading text-xl font-bold tracking-tight">Opening hours</h2>
          <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-1 text-sm sm:grid-cols-3 md:grid-cols-4">
            {DAYS_ORDER.map(([k, label]) => {
              const d = (c.opening_hours || {})[k];
              if (!d || (!d.closed && !(d.open && d.close))) return null;
              return (
                <div key={k} className="flex justify-between gap-2" data-testid={`hub-hours-${k}`}>
                  <span className="font-semibold">{label}</span>
                  <span style={{ color: P.muted }}>{d.closed ? "Closed" : `${d.open}–${d.close}`}</span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {data.menus.length > 0 && (
        <section style={{ backgroundColor: P.card, borderTop: `1px solid ${P.line}` }}>
          <div className="mx-auto max-w-4xl px-6 py-14">
            <h2 className="font-heading text-xl font-bold tracking-tight">Menus</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              {data.menus.map((m) => (
                <div key={m.id} className="p-5" style={{ backgroundColor: pageBg, border: `1px solid ${P.line}` }} data-testid={`hub-menu-${m.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="min-w-0 font-heading text-sm font-bold">{m.name}</h3>
                    {data.company.show_menu_prices !== false && (
                      <span className="shrink-0 font-heading text-sm font-black tabular-nums" style={{ color: accent }}>{fmt(m.price_per_head)}<span className="text-[10px] font-medium text-muted-foreground">/head</span></span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{m.description}</p>
                  <ul className="mt-3 space-y-1.5 border-t border-border pt-3 text-sm">
                    {(m.items || []).map((it, i) => (
                      <li key={i}>
                        {it.name}
                        {(it.allergens || []).length > 0 && (
                          <span className="ml-2 inline-flex flex-wrap gap-1">
                            {it.allergens.map((a) => <span key={a} className="bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-800" data-testid={`allergen-${m.id}-${a}`}>{a}</span>)}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.units.length > 0 && (
        <section className="mx-auto max-w-4xl px-6 py-14">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold tracking-tight"><Truck className="h-5 w-5" /> Our fleet</h2>
          <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-3">
            {data.units.map((u) => (
              <div key={u.id} className="p-4 text-sm" style={{ backgroundColor: P.card, border: `1px solid ${P.line}` }} data-testid={`hub-unit-${u.id}`}>
                <p className="font-semibold">{u.name}</p>
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{u.unit_type} · up to {u.capacity} covers</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section id="enquire" style={{ backgroundColor: (c.theme === "dark") ? "#0a0f1a" : "rgba(15,23,42,0.035)", borderTop: `1px solid ${P.line}` }}>
        <div className="mx-auto max-w-2xl px-6 py-14">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold tracking-tight"><Users className="h-5 w-5" /> Enquire about your event</h2>
          <p className="mt-2 text-sm text-muted-foreground">Tell us the date and numbers — we'll come back with availability and a tailored quote.</p>

          {result ? (
            <div className="mt-6 border border-border bg-white p-8" data-testid="hub-enquiry-success">
              <CheckCircle2 className="h-8 w-8" style={{ color: accent }} />
              <h3 className="mt-3 font-heading text-lg font-bold">Enquiry received</h3>
              {result.date_conflict && (
                <p className="mt-2 border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="hub-date-conflict">
                  Heads up — that date is busy in our diary, but we'll confirm what's possible.
                </p>
              )}
              {result.capacity_warning && (
                <p className="mt-2 border border-blue-400 bg-blue-50 px-3 py-2 text-sm text-blue-800" data-testid="hub-capacity-note">
                  This event is bigger than our own fleet's single-day capacity — partner units will be drafted in.
                </p>
              )}
              {result.estimate ? (
                <>
                  <p className="mt-2 text-sm text-muted-foreground">Here's an instant estimate based on your numbers:</p>
                  <div className="mt-4"><QuoteLines quote={result.estimate} /></div>
                </>
              ) : (
                <p className="mt-2 text-sm text-muted-foreground" data-testid="hub-no-estimate-msg">Thank you — we'll be in touch shortly with a personalised quote.</p>
              )}
              <p className="mt-4 text-xs text-muted-foreground">Estimates exclude external provider add-ons. We'll confirm final pricing by email.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 p-6 sm:grid-cols-2" style={{ backgroundColor: "#ffffff", color: "#0f172a", border: `1px solid ${P.line}` }} data-testid="hub-enquiry-form">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="hp-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
              <div className="space-y-1.5"><Label>Your name</Label><Input data-testid="hub-name-input" required value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input data-testid="hub-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="hub-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Event date</Label>
                <Input data-testid="hub-date-input" type="date" min={new Date().toISOString().split("T")[0]} required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} />
                {dateBlocked && <p className="text-xs font-bold text-red-600" data-testid="hub-date-blocked">Fully booked on this date — please choose another.</p>}
                {dateConflict && <p className="text-xs font-medium text-amber-700" data-testid="hub-date-warning">Limited availability that day — book early.</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Guests</Label>
                <Input data-testid="hub-guests-input" type="number" min="1" required value={form.guest_count} onChange={(e) => setForm({ ...form, guest_count: e.target.value })} />
                {capacityExceeded && <p className="text-xs font-medium text-amber-700" data-testid="hub-capacity-warning">Beyond our single-event fleet capacity — we'll bring in trusted partner units.</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Menu</Label>
                <Select value={form.menu_id} onValueChange={(v) => setForm({ ...form, menu_id: v })}>
                  <SelectTrigger data-testid="hub-menu-select"><SelectValue placeholder="Any / not sure" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {data.menus.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`hub-menu-option-${m.id}`}>{m.name}{data.company.show_menu_prices !== false ? ` — ${fmt(m.price_per_head)}/head` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Your venue / location</Label>
                <Input data-testid="hub-venue-name-input" placeholder="e.g. Home, hired hall, marquee in a field…" value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Venue address / postcode</Label>
                <Input data-testid="hub-venue-address-input" value={form.venue_address} onChange={(e) => setForm({ ...form, venue_address: e.target.value })} />
              </div>
              <div className="col-span-1 space-y-1.5 sm:col-span-2"><Label>Anything else?</Label><Textarea data-testid="hub-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="col-span-1 sm:col-span-2">
                <Button data-testid="hub-submit-button" type="submit" disabled={sending || dateBlocked} className="w-full rounded-md text-white" style={{ backgroundColor: accent }}>
                  <Send className="mr-2 h-4 w-4" /> {sending ? "Sending…" : "Send enquiry"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      {lightbox !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-6" data-testid="gallery-lightbox" onClick={() => setLightbox(null)}>
          <button type="button" data-testid="lightbox-close" aria-label="Close" onClick={() => setLightbox(null)} className="absolute right-5 top-5 p-2 text-white/80 hover:text-white"><X className="h-7 w-7" /></button>
          {c.gallery.length > 1 && (
            <button type="button" data-testid="lightbox-prev" aria-label="Previous photo" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + c.gallery.length) % c.gallery.length); }} className="absolute left-3 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white"><ChevronLeft className="h-10 w-10" /></button>
          )}
          <img src={c.gallery[lightbox]} alt="" data-testid="lightbox-image" onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-full object-contain" />
          {c.gallery.length > 1 && (
            <button type="button" data-testid="lightbox-next" aria-label="Next photo" onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % c.gallery.length); }} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white"><ChevronRight className="h-10 w-10" /></button>
          )}
          <p className="absolute bottom-5 rounded-full bg-black/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-white/90">{lightbox + 1} / {c.gallery.length}</p>
        </div>
      )}

      <footer className="py-6 text-center text-xs uppercase tracking-[0.2em]" style={{ borderTop: `1px solid ${P.line}`, backgroundColor: P.card, color: P.muted }}>
        {(c.website || Object.values(c.socials || {}).some(Boolean)) && (
          <div className="mb-3 flex items-center justify-center gap-5 normal-case tracking-normal" data-testid="hub-links">
            {c.website && <a href={c.website} target="_blank" rel="noreferrer" data-testid="hub-website-link" className="font-semibold" style={{ color: accent }}>Website</a>}
            {c.socials?.facebook && <a href={c.socials.facebook} target="_blank" rel="noreferrer" data-testid="hub-social-facebook" className="hover:underline">Facebook</a>}
            {c.socials?.instagram && <a href={c.socials.instagram} target="_blank" rel="noreferrer" data-testid="hub-social-instagram" className="hover:underline">Instagram</a>}
            {c.socials?.x && <a href={c.socials.x} target="_blank" rel="noreferrer" data-testid="hub-social-x" className="hover:underline">X</a>}
          </div>
        )}
        Powered by CateringDiary.app
      </footer>
    </div>
  );
}
